import { useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { VIDEO_SESSION_HEARTBEAT } from '@/shared/mutations';
import { VIDEO_SESSION_HEARTBEAT_INTERVAL_MS } from '@/shared/constants/videoSession';
import SentryService from '@/services/sentry/Sentry.service';

interface UseVideoHeartbeatProps {
  sessionName: string | null;
  isActive?: boolean;
}

export const useVideoHeartbeat = ({
  sessionName,
  isActive = true,
}: UseVideoHeartbeatProps) => {
  const intervalRef = useRef<NodeJS.Timeout>();

  const [sendHeartbeat] = useMutation(VIDEO_SESSION_HEARTBEAT, {
    onError: (error) => {
      SentryService.captureError('Video session heartbeat failed', error);
    },
  });

  useEffect(() => {
    // Clear any existing interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }

    // Only start heartbeat if we have a session name and it's active
    if (!sessionName || !isActive) {
      return;
    }

    // Send initial heartbeat immediately
    sendHeartbeat({
      variables: {
        input: { sessionName },
      },
    })
      .then(() => {
        __DEV__ &&
          console.log('Initial video session heartbeat sent', sessionName);
      })
      .catch((error) => {
        SentryService.captureError(
          'Initial video session heartbeat failed',
          error,
        );
      });

    // Set up recurring heartbeat
    intervalRef.current = setInterval(() => {
      sendHeartbeat({
        variables: {
          input: { sessionName },
        },
      })
        .then(() => {
          __DEV__ && console.log('Video session heartbeat sent', sessionName);
        })
        .catch((error) => {
          SentryService.captureError('Video session heartbeat failed', error);
        });
    }, VIDEO_SESSION_HEARTBEAT_INTERVAL_MS);

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
        __DEV__ && console.log('Video session heartbeat stopped');
      }
    };
  }, [sessionName, isActive, sendHeartbeat]);

  return {
    stopHeartbeat: () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
    },
  };
};
